import { type Dispatch, type SetStateAction, useEffect, useState } from "react";

export type GlobalState<T> = {
    state: T;
    stateDispatchers: Dispatch<SetStateAction<T>>[];
};

// Ideally, new Map<string, GlobalState<T>>();
const globalStateKeyedStore = new Map<string, any>();

function createGlobalState<T>(stateKey: string, initialState: T | undefined | null) {
    globalStateKeyedStore.set(stateKey, {
        state: initialState,
        stateDispatchers: [],
    });
}

function useGlobalState<T>(stateKey: string, initialState: T) {
    // workaround casting for generics map value any type
    const storeAsValue = globalStateKeyedStore.get(stateKey);

    if (!storeAsValue) {
        createGlobalState(stateKey, initialState);
    }
    const store = globalStateKeyedStore.get(stateKey) as GlobalState<T>;

    // create store for new subscriber, init with existing store state
    const [newStoreState, setNewStoreState] = useState<T>(store.state);

    useEffect(() => {
        // Called just after component mount
        store.stateDispatchers.push(setNewStoreState);
        // Called just before the component unmount
        return () => {
            store.stateDispatchers = store.stateDispatchers.filter(
                (listener) => listener !== setNewStoreState,
            );
        };
    }, []);
    // update store state with the new state key value
    store.state = { ...store.state, ...newStoreState };
    // dispatch changes to all the listeners
    const allStateDispatchers = (value: T) => {
        store.stateDispatchers.forEach((dispatcher) => {
            dispatcher(value);
        });
    };
    return { globalState: store.state, setGlobalState: allStateDispatchers };
}

export default useGlobalState;
